import { useEffect, useState } from 'react';
import type { UpdateTwilioVoiceSettingsInput } from '@klaro7/validators';
import { Badge, Button, Input, Spinner } from '@klaro7/ui';

import { usePinGate } from '@/components/PinGate';
import {
  useTwilioVoiceSettings,
  useUpdateTwilioVoiceSettings,
  useSendTestVoiceCall,
} from '../hooks/useServicesSettings';

function extractErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = err as {
      response?: {
        data?: { error?: { message?: string }; message?: string };
        status?: number;
        statusText?: string;
      };
    };
    const apiMsg = resp.response?.data?.error?.message || resp.response?.data?.message;
    const status = resp.response?.status;
    if (apiMsg) return `[${status}] ${apiMsg}`;
    if (status) return `HTTP ${status}: ${resp.response?.statusText || 'Errore sconosciuto'}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Twilio Voice configuration. Super-admin only — the parent gates rendering.
 *
 * authToken arrives masked from the server and is included in the PUT payload only
 * when the admin types a new value (mirrors Stripe/SMS/WebRTC), so a stored secret
 * is never overwritten with its mask. fromNumber is the E.164 caller-ID.
 */
export function TwilioVoiceSettingsSection() {
  const { requirePin, pinDialog } = usePinGate();
  const { data: settings, isLoading } = useTwilioVoiceSettings(true);
  const updateMutation = useUpdateTwilioVoiceSettings();
  const testMutation = useSendTestVoiceCall();

  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [voiceMessage, setVoiceMessage] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [voice, setVoice] = useState('Polly.Bianca');
  const [enabled, setEnabled] = useState(false);
  const [savedAuthToken, setSavedAuthToken] = useState('');

  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setAccountSid(settings.accountSid ?? '');
      setAuthToken(settings.authToken ?? '');
      setFromNumber(settings.fromNumber ?? '');
      setVoiceMessage(settings.voiceMessage ?? '');
      setAudioUrl(settings.audioUrl ?? '');
      setVoice(settings.voice ?? 'Polly.Bianca');
      setEnabled(settings.enabled ?? false);
      setSavedAuthToken(settings.authToken ?? '');
    }
  }, [settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 15000 : 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleSave() {
    // Stessa regola imposta dal server (updateTwilioVoiceSettingsSchema): un
    // messaggio personalizzato senza {utente} annuncerebbe lo stesso nome a
    // ogni chiamata (incidente "Vincenzo" del 2026-07-07).
    if (voiceMessage.trim() !== '' && !/\{utente\}/i.test(voiceMessage)) {
      setToast({
        type: 'error',
        message:
          'Il messaggio vocale deve contenere il placeholder {utente}: viene sostituito col nome del chiamante. Non usare un nome fisso.',
      });
      return;
    }
    const payload: UpdateTwilioVoiceSettingsInput = {
      accountSid,
      fromNumber,
      voiceMessage,
      audioUrl,
      voice,
      enabled,
    };
    if (authToken !== savedAuthToken) {
      payload.authToken = authToken;
    }
    try {
      await updateMutation.mutateAsync(payload);
      setToast({ type: 'success', message: 'Configurazione Twilio Voice salvata con successo' });
    } catch (err) {
      setToast({ type: 'error', message: `Errore nel salvataggio Twilio Voice: ${extractErrorDetail(err)}` });
    }
  }

  async function handleTest() {
    setTestResult(null);
    try {
      const res = await testMutation.mutateAsync({ to: testTo.trim() });
      setTestResult({ ok: true, detail: `Chiamata avviata verso ${res.to} (sid ${res.sid}, ${res.status}). Il telefono dovrebbe squillare.` });
    } catch (err) {
      setTestResult({ ok: false, detail: extractErrorDetail(err) });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Twilio Voice (chiamata vocale)</h3>
            <p className="text-xs text-neutral-600">Chiamate vocali in uscita tramite Twilio (solo super admin)</p>
          </div>
        </div>
        <Badge variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Attivo' : 'Disattivato'}
        </Badge>
      </div>

      <div className="space-y-4 p-5">
        {toast && (
          <div
            className={`rounded-lg border px-4 py-2.5 text-sm font-medium ${
              toast.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {toast.message}
          </div>
        )}

        <Input
          label="Account SID"
          value={accountSid}
          onChange={(e) => setAccountSid(e.target.value)}
          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          helperText="Account SID del progetto Twilio. Salvato in chiaro."
        />

        <Input
          label="Auth Token"
          type="password"
          value={authToken}
          onChange={(e) => setAuthToken(e.target.value)}
          placeholder="••••••••••••••••"
          helperText="Auth Token Twilio, cifrato. Lascia invariato il valore mascherato per non sovrascriverlo."
        />

        <Input
          label="Numero mittente"
          value={fromNumber}
          onChange={(e) => setFromNumber(e.target.value)}
          placeholder="+39…"
          helperText="Numero caller-ID in formato E.164 (es. +39…). Salvato in chiaro."
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">
            Messaggio vocale (testo TTS)
          </label>
          <textarea
            value={voiceMessage}
            onChange={(e) => setVoiceMessage(e.target.value)}
            rows={3}
            placeholder="Ciao, l'utente {utente} ti sta chiamando su Klaro7. Clicca sul link ricevuto via SMS per rispondere subito."
            className="flex w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
          />
          <p className="mt-1 text-xs text-neutral-500">
            Letto da una voce sintetica. Usa <code>{'{utente}'}</code> per inserire il nome del
            chiamante. Ignorato se imposti un URL audio qui sotto.
          </p>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">Voce TTS</label>
          <select
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            className="h-10 w-full rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
          >
            <option value="Polly.Bianca">Bianca (IT, femminile) — consigliata</option>
            <option value="Polly.Giorgio">Giorgio (IT, maschile)</option>
            <option value="alice">Alice (legacy)</option>
          </select>
        </div>

        <Input
          label="URL audio (opzionale)"
          value={audioUrl}
          onChange={(e) => setAudioUrl(e.target.value)}
          placeholder="https://…/messaggio.mp3"
          helperText="Se valorizzato, riproduce questo file MP3/WAV (deve essere pubblico) invece del testo TTS."
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
          />
          Abilitato
        </label>

        <p className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3 text-xs text-neutral-600">
          Twilio Voice consente di effettuare chiamate vocali in uscita verso i numeri di
          telefono. L&apos;Auth Token è cifrato a riposo. Il numero mittente deve essere un
          numero E.164 verificato sul tuo account Twilio.
        </p>

        {/* Test call — uses the SAVED config, so salva prima di testare. */}
        <div className="rounded-lg border border-neutral-200 bg-neutral-50/60 p-4">
          <p className="text-sm font-medium text-neutral-700">Testa chiamata</p>
          <p className="mt-1 text-xs text-neutral-500">
            Avvia una chiamata reale verso il numero indicato usando la configurazione
            <strong> salvata</strong> (salva prima eventuali modifiche). Funziona anche se
            &quot;Abilitato&quot; è spento.
          </p>
          <div className="mt-3 flex items-end gap-3">
            <div className="flex-1">
              <Input
                label="Numero da chiamare"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="+39…"
              />
            </div>
            <Button
              variant="outline"
              onClick={handleTest}
              loading={testMutation.isPending}
              disabled={!/^\+[1-9]\d{6,14}$/.test(testTo.trim())}
            >
              Testa chiamata
            </Button>
          </div>
          {testResult && (
            <div
              className={`mt-3 rounded-lg border px-3 py-2 text-xs font-medium ${
                testResult.ok
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}
            >
              {testResult.detail}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-neutral-100 pt-4">
          {pinDialog}
          <Button onClick={() => requirePin(handleSave)} loading={updateMutation.isPending}>
            Salva Twilio Voice
          </Button>
        </div>
      </div>
    </div>
  );
}
